"use client"

/**
 * "Call askFinz" dialog — launched from the phone button in the chat drawer
 * and the chat.askfinz.ai composer.
 *
 * Flow: shows per-minute pricing → user requests a call (inbound: they dial
 * +1 650 447 1414 and key in a one-time 6-digit PIN; callback: we ring their
 * VERIFIED number when it's their turn — no hold time) → live queue position
 * polls every 3 s with a visual indicator. Requires ≥ €5 of credits.
 *
 * Built on the shared Radix Dialog primitives (solid themed surface,
 * light/dark via standard tokens) — NOT the drawer-scoped CSS variables.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import { Phone, PhoneCall, PhoneIncoming, Loader2, Users, CheckCircle2, PhoneOff, RotateCcw } from "lucide-react"
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../ui/dialog"
import { Button } from "../ui/button"

const ACCOUNT_API = "https://account.askfinz.ai"

interface RateBreakdown {
  call_eur_min: number
  stt_eur_min: number
  tts_eur_min: number
  llm_eur_min: number
  service_fee_pct: number
  total_eur_min: number
}

interface CallRequest {
  request_id: string
  pin: string
  mode: "inbound" | "callback"
  status: string
  number: string
  queue_position: number
  rate_eur_per_min: number
  rate_breakdown: RateBreakdown | null
  billed_seconds?: number
  billed_eur?: number
}

const TERMINAL = ["completed", "cancelled", "expired", "failed"]

interface VoiceCallDialogProps {
  open: boolean
  onClose: () => void
  model?: string
  getAuthToken?: () => string | null
}

export function VoiceCallDialog({ open, onClose, model, getAuthToken }: VoiceCallDialogProps) {
  const [mode, setMode] = useState<"inbound" | "callback">("inbound")
  const [rates, setRates] = useState<RateBreakdown | null>(null)
  const [phoneVerified, setPhoneVerified] = useState(false)
  const [request, setRequest] = useState<CallRequest | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const authedFetch = useCallback(async (path: string, init?: RequestInit) => {
    const token = getAuthToken?.() ?? null
    return fetch(`${ACCOUNT_API}${path}`, {
      ...init,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init?.headers || {}),
      },
    })
  }, [getAuthToken])

  useEffect(() => {
    if (!open) return
    let alive = true
    ;(async () => {
      try {
        const res = await authedFetch(`/api/account/voice/rates?mode=${mode}&model=${encodeURIComponent(model || "")}`)
        if (!alive) return
        if (res.ok) {
          const data = await res.json()
          setRates(data.breakdown)
          setPhoneVerified(!!data.phone_verified)
        }
      } catch { /* preview is best-effort */ }
    })()
    return () => { alive = false }
  }, [open, mode, model, authedFetch])

  useEffect(() => {
    if (!request || ["completed", "cancelled", "expired", "failed"].includes(request.status)) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
      return
    }
    pollRef.current = setInterval(async () => {
      try {
        const res = await authedFetch(`/api/account/voice/call-status?request_id=${request.request_id}`)
        if (res.ok) setRequest(await res.json())
      } catch { /* keep last known state */ }
    }, 3000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [request?.request_id, request?.status, authedFetch])

  const startRequest = async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await authedFetch("/api/account/voice/call-request", {
        method: "POST",
        body: JSON.stringify({ mode, model: model || null }),
      })
      const data = await res.json().catch(() => ({}))
      if (res.ok) setRequest(data)
      else setError(data?.detail || "Could not create the call request.")
    } catch {
      setError("Could not reach the calling service.")
    } finally {
      setBusy(false)
    }
  }

  const cancelRequest = async () => {
    if (!request) return
    try {
      await authedFetch("/api/account/voice/call-cancel", {
        method: "POST",
        body: JSON.stringify({ request_id: request.request_id }),
      })
    } catch { /* best effort */ }
    setRequest(null)
  }

  const ahead = request ? Math.max(0, request.queue_position - 1) : 0
  const live = request?.status === "active"
  const terminal = request ? TERMINAL.includes(request.status) : false

  const startNewCall = () => {
    // Old PIN is dead once the call ends — drop it and return to the form so a
    // fresh request (new PIN) can be created.
    setRequest(null)
    setError(null)
  }

  const fmtDuration = (s: number) => {
    const m = Math.floor(s / 60)
    const sec = s % 60
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-primary" /> Call askFinz
          </DialogTitle>
          <DialogDescription>
            Continue this conversation by phone. Charges come off your credits
            per minute, only while the call is live.
          </DialogDescription>
        </DialogHeader>

        {request && terminal ? (
          <div className="space-y-3">
            <div className="rounded-lg border bg-muted/40 p-4 text-center">
              <PhoneOff className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
              <div className="text-sm font-semibold">
                {request.status === "completed" ? "Call ended" :
                 request.status === "cancelled" ? "Request cancelled" :
                 request.status === "expired" ? "Request expired" : "Call failed"}
              </div>
              {request.status === "completed" && (
                <div className="mt-2 space-y-0.5 text-sm">
                  <div className="text-muted-foreground">
                    Duration: <span className="font-medium text-foreground">{fmtDuration(request.billed_seconds || 0)}</span>
                  </div>
                  <div className="text-muted-foreground">
                    Credits used: <span className="font-medium text-foreground">€{(request.billed_eur || 0).toFixed(4)}</span>
                  </div>
                </div>
              )}
              <div className="mt-2 text-[11px] text-muted-foreground">
                Your PIN <span className="font-mono">{request.pin}</span> is no longer valid.
              </div>
            </div>
            <Button className="w-full gap-2" onClick={startNewCall}>
              <RotateCcw className="h-4 w-4" /> Start a new call
            </Button>
          </div>
        ) : !request ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setMode("inbound")}
                className={`flex items-start gap-2 rounded-lg border p-2.5 text-left text-sm transition-colors ${mode === "inbound" ? "border-primary bg-primary/10" : "border-border hover:bg-muted"}`}
              >
                <PhoneCall className="mt-0.5 h-4 w-4 shrink-0" />
                <span><span className="font-semibold">I'll call</span><br />
                  <span className="text-xs text-muted-foreground">Dial in with a PIN; hold if busy</span></span>
              </button>
              <button
                onClick={() => phoneVerified && setMode("callback")}
                disabled={!phoneVerified}
                className={`flex items-start gap-2 rounded-lg border p-2.5 text-left text-sm transition-colors ${mode === "callback" ? "border-primary bg-primary/10" : "border-border hover:bg-muted"} ${!phoneVerified ? "cursor-not-allowed opacity-50" : ""}`}
                title={phoneVerified ? "" : "Verify your phone number on your profile first"}
              >
                <PhoneIncoming className="mt-0.5 h-4 w-4 shrink-0" />
                <span><span className="font-semibold">Call me</span><br />
                  <span className="text-xs text-muted-foreground">{phoneVerified ? "No hold — we ring you when it's your turn" : "Needs a verified phone number"}</span></span>
              </button>
            </div>

            {rates && (
              <div className="rounded-lg border bg-muted/40 p-3 text-xs">
                <div className="mb-1 text-sm font-semibold">
                  €{rates.total_eur_min.toFixed(4)} / minute
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-muted-foreground">
                  <span>Call ({mode === "callback" ? "to your number" : "line"} +{rates.service_fee_pct}% fee)</span><span className="text-right">€{rates.call_eur_min.toFixed(4)}</span>
                  <span>Speech-to-text</span><span className="text-right">€{rates.stt_eur_min.toFixed(4)}</span>
                  <span>Text-to-speech</span><span className="text-right">€{rates.tts_eur_min.toFixed(4)}</span>
                  <span>AI model{rates.llm_eur_min === 0 ? " (free)" : ""}</span><span className="text-right">€{rates.llm_eur_min.toFixed(4)}</span>
                </div>
                <div className="mt-1.5 text-[11px] text-muted-foreground">
                  Minimum balance: €5.00 in credits. Billed per second of call time.
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-2.5 text-xs text-destructive">
                {error}{error.includes("€5") && (
                  <> <a className="underline" href="https://account.askfinz.ai/credits" target="_blank" rel="noreferrer">Top up credits</a></>
                )}
              </div>
            )}

            <Button className="w-full" onClick={startRequest} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "inbound" ? "Get my PIN" : "Request a callback"}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="rounded-lg border bg-muted/40 p-3 text-center">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                {request.mode === "inbound" ? "Call this number" : "We'll call you from"}
              </div>
              <a href={`tel:${request.number.replace(/\s/g, "")}`} className="text-lg font-bold hover:text-primary">
                {request.number}
              </a>
              <div className="mt-2 text-xs uppercase tracking-wide text-muted-foreground">Your one-time PIN</div>
              <div className="font-mono text-3xl font-bold tracking-[0.4em] text-primary">{request.pin}</div>
              {request.mode === "inbound" && (
                <div className="mt-2 text-[11px] text-muted-foreground">
                  When the menu answers, press <span className="font-semibold text-foreground">1</span> for the AI assistant, then enter this PIN.
                </div>
              )}
            </div>

            <div className="rounded-lg border p-3">
              {live ? (
                <div className="flex items-center gap-2 text-sm font-semibold text-green-600 dark:text-green-500">
                  <CheckCircle2 className="h-4 w-4" /> You're connected — talk away!
                </div>
              ) : request.status === "dialing" ? (
                <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                  <Loader2 className="h-4 w-4 animate-spin" /> Calling you now — answer and enter your PIN.
                </div>
              ) : (
                <>
                  <div className="mb-1.5 flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-primary" />
                    {ahead === 0
                      ? (request.mode === "inbound" ? "You're next — call now!" : "You're next — keep your phone handy.")
                      : `${ahead} caller${ahead === 1 ? "" : "s"} ahead of you`}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {Array.from({ length: Math.min(Math.max(request.queue_position, 1), 8) }).map((_, i) => (
                      <span key={i} className={`h-2.5 w-2.5 rounded-full ${i === 0 ? "animate-pulse bg-primary" : "bg-border"}`} />
                    ))}
                    <span className="ml-1 text-[11px] text-muted-foreground">live — updates automatically</span>
                  </div>
                </>
              )}
            </div>

            <div className="text-center text-xs text-muted-foreground">
              €{request.rate_eur_per_min.toFixed(4)}/min · billed from your credits while connected
            </div>

            {!live && (
              <Button variant="outline" className="w-full" onClick={cancelRequest}>
                Cancel request
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
